import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Lock, Check, Users, ChevronLeft, RefreshCw, Calendar as CalendarIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIN_STORAGE = "staff_pin_saved";

const AREAS = {
  cucina: { label: "Cucina", color: "#7A1F1A" },
  sala: { label: "Sala", color: "#4a1c17" },
  piani: { label: "Piani", color: "#8B5A2B" },
  reception: { label: "Reception", color: "#C48B31" },
  manutenzione: { label: "Manutenzione", color: "#5A5A5A" },
  altro: { label: "Altro", color: "#666" },
};
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const SLOT_LABEL = { mattina: "Mattina", pranzo: "Pranzo", pomeriggio: "Pomeriggio", sera: "Sera", sempre: "" };

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
}

export default function StaffPage() {
  const [pin, setPin] = useState(() => localStorage.getItem(PIN_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");

  // Verify pin on load
  useEffect(() => {
    (async () => {
      if (!pin) { setChecking(false); return; }
      try {
        await axios.post(`${API}/staff/verify-pin`, { pin });
        setAuthed(true);
      } catch {
        localStorage.removeItem(PIN_STORAGE);
        setPin("");
      } finally { setChecking(false); }
    })();
  }, []);

  async function login(e) {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${API}/staff/verify-pin`, { pin });
      localStorage.setItem(PIN_STORAGE, pin);
      setAuthed(true);
    } catch {
      setError("PIN non valido");
    }
  }

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-[#F7EBDF]"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>;

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F7EBDF] flex items-center justify-center p-4">
        <form onSubmit={login} className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
          <div className="flex flex-col items-center text-center gap-2 mb-4">
            <div className="h-16 w-16 rounded-full bg-[#4a1c17] flex items-center justify-center"><Lock className="h-7 w-7 text-white" /></div>
            <h1 className="text-2xl font-bold text-[#4a1c17]">Area Team</h1>
            <p className="text-sm text-neutral-600">Il Pozzo dei Giganti</p>
          </div>
          <label className="text-sm block">PIN
            <input
              data-testid="team-pin-input"
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={e => setPin(e.target.value)}
              className="w-full border border-[#e6d3c0] rounded-lg px-3 py-3 mt-1 text-center text-2xl tracking-widest font-mono"
              autoFocus
            />
          </label>
          {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
          <button data-testid="team-pin-submit" type="submit" className="w-full mt-4 bg-[#4a1c17] text-white rounded-lg py-3 font-medium">Entra</button>
        </form>
      </div>
    );
  }

  return <StaffHome pin={pin} onLogout={() => { localStorage.removeItem(PIN_STORAGE); setAuthed(false); setPin(""); }} />;
}

function StaffHome({ pin, onLogout }) {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWeek, setShowWeek] = useState(false);

  const headers = { "X-STAFF-PIN": pin };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/staff/members`, { headers });
        setMembers(r.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7EBDF]"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>;

  if (showWeek) return <WeekView pin={pin} onBack={() => setShowWeek(false)} />;
  if (selected) return <MemberDay pin={pin} member={selected} onBack={() => setSelected(null)} />;

  const byArea = members.reduce((acc, m) => { (acc[m.area] = acc[m.area] || []).push(m); return acc; }, {});

  return (
    <div className="min-h-screen bg-[#F7EBDF] pb-20">
      <header className="bg-[#4a1c17] text-white p-4 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-80">Il Pozzo dei Giganti</div>
            <h1 className="text-2xl font-bold">Team & Attività</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowWeek(true)} data-testid="team-week-btn" className="p-2 bg-white/10 rounded-lg" title="Calendario settimana"><CalendarIcon className="h-5 w-5" /></button>
            <button onClick={onLogout} className="text-xs bg-white/10 px-3 py-2 rounded-lg">Esci</button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-5">
        <div className="text-sm text-neutral-700">Ciao 👋 seleziona il tuo profilo per vedere le attività del giorno.</div>

        {Object.entries(AREAS).map(([key, meta]) => {
          const list = byArea[key] || [];
          if (list.length === 0) return null;
          return (
            <div key={key}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
                <h2 className="font-bold text-[#4a1c17]">{meta.label}</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {list.map(m => (
                  <button
                    key={m.id}
                    data-testid={`team-member-${m.name}`}
                    onClick={() => setSelected(m)}
                    className="bg-white rounded-xl p-3 flex flex-col items-center gap-2 shadow-sm active:scale-95 transition"
                  >
                    <div className="h-14 w-14 rounded-full flex items-center justify-center text-white text-xl font-bold" style={{ backgroundColor: m.color || meta.color }}>
                      {m.initials || m.name?.[0]}
                    </div>
                    <div className="text-sm font-bold text-[#4a1c17] text-center leading-tight">{m.name}</div>
                    <div className="text-[10px] text-neutral-600 text-center leading-tight line-clamp-2">{m.role}</div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </main>
    </div>
  );
}

function MemberDay({ pin, member, onBack }) {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState(new Set());
  const headers = { "X-STAFF-PIN": pin };

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/staff/today?member_id=${member.id}&date=${date}`, { headers });
      setData(r.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  async function toggle(task) {
    const s = new Set(savingIds); s.add(task.id); setSavingIds(s);
    try {
      await axios.post(`${API}/staff/complete`, {
        task_id: task.id,
        date_str: date,
        completed_by: member.name,
        done: !task.done,
      }, { headers });
      // Optimistic update
      setData(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => t.id === task.id ? { ...t, done: !t.done, completed_by: !t.done ? member.name : null } : t),
      }));
    } finally {
      const s2 = new Set(savingIds); s2.delete(task.id); setSavingIds(s2);
    }
  }

  const bySlot = (data?.tasks || []).reduce((acc, t) => { (acc[t.slot || "sempre"] = acc[t.slot || "sempre"] || []).push(t); return acc; }, {});
  const doneCount = (data?.tasks || []).filter(t => t.done).length;
  const total = data?.tasks?.length || 0;

  return (
    <div className="min-h-screen bg-[#F7EBDF] pb-20">
      <header className="bg-[#4a1c17] text-white p-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={onBack} data-testid="team-back-btn"><ChevronLeft className="h-6 w-6" /></button>
          <div className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold border-2 border-white/40" style={{ backgroundColor: member.color }}>
            {member.initials || member.name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold">{member.name}</div>
            <div className="text-xs opacity-80 truncate">{member.role}</div>
          </div>
          <button onClick={load} className="p-2 bg-white/10 rounded-lg"><RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            data-testid="team-date-input"
            className="flex-1 border border-[#e6d3c0] rounded-lg px-3 py-2 bg-white"
          />
          <div className="text-sm font-bold text-[#4a1c17] tabular-nums">{doneCount}/{total}</div>
        </div>

        <div className="text-sm text-neutral-700 capitalize">{fmtDate(date)}</div>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>
        ) : total === 0 ? (
          <div className="text-center py-16 text-neutral-500">Nessuna attività per questo giorno 🎉</div>
        ) : (
          ["mattina", "pranzo", "pomeriggio", "sera", "sempre"].map(slot => {
            const list = bySlot[slot] || [];
            if (list.length === 0) return null;
            return (
              <div key={slot}>
                {SLOT_LABEL[slot] && <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">{SLOT_LABEL[slot]}</div>}
                <div className="space-y-2">
                  {list.map(t => (
                    <button
                      key={t.id}
                      data-testid={`team-task-${t.id}`}
                      onClick={() => toggle(t)}
                      disabled={savingIds.has(t.id)}
                      className={`w-full text-left bg-white rounded-xl p-3 flex items-start gap-3 shadow-sm active:scale-[0.98] transition ${t.done ? "opacity-60" : ""}`}
                    >
                      <div className={`h-6 w-6 mt-0.5 rounded-md flex items-center justify-center flex-shrink-0 ${t.done ? "bg-[#4a1c17] text-white" : "border-2 border-[#4a1c17]"}`}>
                        {t.done && <Check className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium text-[#4a1c17] ${t.done ? "line-through" : ""}`}>{t.title}</div>
                        {t.description && <div className="text-xs text-neutral-600 mt-0.5">{t.description}</div>}
                        {t.completed_by && t.done && <div className="text-[11px] text-neutral-500 mt-1">Completato da {t.completed_by}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </main>
    </div>
  );
}

function WeekView({ pin, onBack }) {
  const [week, setWeek] = useState(null);
  const headers = { "X-STAFF-PIN": pin };
  useEffect(() => {
    (async () => {
      const r = await axios.get(`${API}/staff/week`, { headers });
      setWeek(r.data);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#F7EBDF] pb-20">
      <header className="bg-[#4a1c17] text-white p-4 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button onClick={onBack} data-testid="team-week-back"><ChevronLeft className="h-6 w-6" /></button>
          <div>
            <div className="text-xs opacity-80">Calendario</div>
            <h1 className="text-xl font-bold">Presenze settimana</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-3">
        {!week ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div> : (
          week.days.map(d => (
            <div key={d.date} className={`bg-white rounded-xl p-3 border ${d.dow === 1 ? "border-red-300 bg-red-50/50" : "border-[#e6d3c0]"}`}>
              <div className="flex items-baseline justify-between">
                <div className="font-bold text-[#4a1c17] capitalize">{d.day_name} <span className="text-xs text-neutral-500 font-normal">{d.date.slice(8, 10)}/{d.date.slice(5, 7)}</span></div>
                {d.dow === 1 && <span className="text-[10px] font-bold text-red-700">CHIUSURA</span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {d.slots.filter(s => !s.off).map((s, i) => (
                  <div key={i} className="text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ backgroundColor: (s.color || "#4a1c17") + "22", color: "#4a1c17" }}>
                    <span className="font-bold">{s.name}</span>
                    <span>·</span>
                    <span>{s.start}–{s.end}</span>
                  </div>
                ))}
                {d.slots.filter(s => !s.off).length === 0 && <span className="text-xs text-neutral-500 italic">Nessuno in servizio</span>}
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
}
